import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { awinTracking, bingAds, criteo, facebookPixel, hotjarTracking, linkedinPixel } from '../../server/html';
import { useConfig } from '../../config';
import { MetaContainer } from '../../state/meta';
import { CUSTOMER_INFO_COOKIE, useSession } from '../../state/session';
import { plausibleIsOnThisPage, tagPlausibleCustomer } from '../../hooks/useAnalytics';
import { generateUUID } from '../utils';
import { cookieService } from '../cookieService';

declare global {
	interface Window {
		Cookiebot: any;
	}
}

type ScriptConfig = {
	scriptContent: ScriptContent;
	consentCategory: 'statistics' | 'marketing' | 'preferences' | 'necessary';
	initFunction?: () => void;
};

export type ScriptContent =
	| {
			script?: { src: string; type?: string; defer?: boolean; async?: boolean };
			noscript?: string;
			js?: string;
	  }
	| undefined;

export type TrackingConsents = {
	statistics: boolean;
	marketing: boolean;
	preferences: boolean;
	necessary: boolean;
};

export const ConsentMode = () => {
	const [consents, setConsents] = useState<TrackingConsents>({
		marketing: false,
		necessary: false,
		preferences: false,
		statistics: false,
	});
	const config = useConfig();
	const meta = MetaContainer.useContainer();
	const { user, isLoggedIn } = useSession();
	// whether the visitor has actually answered the dialog, as opposed to not having been asked yet
	const [consentAnswered, setConsentAnswered] = useState(false);

	// the plausible customer marker is analytics state, so it follows the statistics consent the way
	// the search profile follows preferences. it records when the sign-in was first seen, not when
	// it was last seen, so an account an order just created does not make its owner an existing
	// customer that same day. the cookie is shared with staging and draft previews, which carry no
	// tracker: an explicit no ends the marker from any of them, while an unanswered dialog — the
	// normal state on staging — must not, and only a page with the tracker ever writes it
	useEffect(() => {
		if (!meta.cookiebotLoaded) return;
		if (consentAnswered && !consents.statistics) {
			cookieService.remove(CUSTOMER_INFO_COOKIE, { siteWide: true });
			tagPlausibleCustomer('new');
		} else if (
			consents.statistics &&
			plausibleIsOnThisPage() &&
			isLoggedIn &&
			!cookieService.read(CUSTOMER_INFO_COOKIE)
		) {
			cookieService.write(CUSTOMER_INFO_COOKIE, String(Date.now()), 400, { siteWide: true });
		}
	}, [meta.cookiebotLoaded, consentAnswered, consents.statistics, isLoggedIn]);

	const trackingScriptConfigs: ScriptConfig[] = [
		{
			scriptContent: bingAds(config),
			consentCategory: 'marketing',
			initFunction: () => {},
		},
		{
			scriptContent: hotjarTracking(config),
			consentCategory: 'marketing',
			initFunction: () => {},
		},
		{
			scriptContent: criteo(config),
			consentCategory: 'marketing',
			initFunction: () => {},
		},
		{
			scriptContent: facebookPixel(config),
			consentCategory: 'marketing',
			initFunction: () => {},
		},
		{
			scriptContent: linkedinPixel(config),
			consentCategory: 'marketing',
			initFunction: () => {},
		},
		{
			scriptContent: awinTracking(config),
			consentCategory: 'marketing',
			initFunction: () => {},
		},
	];

	useEffect(() => {
		meta.setRandomizeSearchProfileId(true);
		meta.setSearchProfileId(generateUUID());
	}, []);

	useEffect(() => {
		const updateConsents = () => {
			meta.setCookiebotLoaded(false);
			if (user?.sp) {
				meta.setSearchProfileId(user.sp);
				meta.setRandomizeSearchProfileId(false);
			}

			if (window.Cookiebot) {
				meta.setCookiebotLoaded(true);
				const updatedConsents: TrackingConsents = {
					statistics: window.Cookiebot.consent.statistics,
					marketing: window.Cookiebot.consent.marketing,
					preferences: window.Cookiebot.consent.preferences,
					necessary: window.Cookiebot.consent.necessary,
				};
				setConsents(updatedConsents);
				setConsentAnswered(!!window.Cookiebot.hasResponse);
				meta.setTrackingConsents(updatedConsents);

				// loop54 search profile id
				if (user?.sp) {
					meta.setSearchProfileId(user.sp);
					meta.setRandomizeSearchProfileId(false);
				} else {
					if (updatedConsents.preferences) {
						meta.setRandomizeSearchProfileId(false);
						const searchProfileCookie = cookieService.read('sp');
						if (!searchProfileCookie) {
							const spId = generateUUID();
							cookieService.write('sp', spId, 400);
							meta.setSearchProfileId(spId);
						} else {
							meta.setSearchProfileId(searchProfileCookie);
						}
					} else {
						meta.setSearchProfileId(generateUUID());
						meta.setRandomizeSearchProfileId(true);
					}
				}
			}
		};

		window.addEventListener('CookiebotOnConsentReady', updateConsents);
		window.addEventListener('CookiebotOnConsentChanged', updateConsents);

		// initial consents check
		updateConsents();

		meta.setTrackingConsents(consents);

		return () => {
			window.removeEventListener('CookiebotOnConsentReady', updateConsents);
			window.removeEventListener('CookiebotOnConsentChanged', updateConsents);
		};
	}, [user]);

	return (
		<>
			{trackingScriptConfigs.map((config, i) => {
				if (consents[config.consentCategory]) {
					const { scriptContent } = config;

					return (
						<Helmet key={i}>
							{scriptContent?.script && (
								<script
									src={scriptContent.script.src}
									type={scriptContent.script.type}
									defer={scriptContent.script.defer}
									async={scriptContent.script.async}
								></script>
							)}
							{scriptContent?.js && <script>{scriptContent.js}</script>}
							{scriptContent?.noscript && <noscript>{scriptContent.noscript}</noscript>}
						</Helmet>
					);
				}
				return null;
			})}
		</>
	);
};
